// PH-50 — the shops this device has been opened for, so a staff member covering two shops can
// switch between them without re-opening each shop's link.
//
// What goes on the list, and what does not (the ticket's own rule): a shop is added ONLY when
// this device actually resolves that shop's slug from a link — `/s/<slug>/` or `?t=<slug>` —
// which is `applyTenantResolution()`'s job, the one place such a link is read. Nothing a person
// can type adds a shop here. The one other entry point is the slug the device ALREADY held when a
// different shop's link arrives: that slug was itself put there by an earlier link, or by an
// Owner Account sign-in / Settings backfill, both of which prove more than a link does.
//
// The list is a convenience, never a source of identity: the slug on it is only ever used to
// navigate to that shop's own `/s/<slug>/` link, which then goes through exactly the same
// resolution and PIN screen as opening the link by hand. A name on the list is a display label
// learned from that shop's own config after it loaded, never trusted for anything else.
//
// Pure functions over an injected Storage, so tests need no browser.

export const KNOWN_SHOPS_KEY = 'pos_known_shops';
export const MAX_KNOWN_SHOPS = 10;
const MAX_NAME_LEN = 80;

// Same shape rule as tenantResolution.js (and the backend's _SLUG_RE). Re-checked on READ as
// well as write, so a hand-edited or corrupted list can never feed a malformed slug into a URL.
const SLUG_SHAPE_RE = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;

const cleanSlug = (raw) => {
  const s = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  return SLUG_SHAPE_RE.test(s) ? s : '';
};

const cleanName = (raw) => (typeof raw === 'string' ? raw.trim().slice(0, MAX_NAME_LEN) : '');

/** @returns {{slug: string, name: string}[]} newest first, deduplicated, never throws */
export function readKnownShops(storage) {
  let parsed;
  try {
    parsed = JSON.parse(storage.getItem(KNOWN_SHOPS_KEY) || '[]');
  } catch (_) {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const seen = new Set();
  const out = [];
  for (const entry of parsed) {
    const slug = cleanSlug(entry && entry.slug);
    if (!slug || seen.has(slug)) continue;
    seen.add(slug);
    out.push({ slug, name: cleanName(entry.name) });
    if (out.length >= MAX_KNOWN_SHOPS) break;
  }
  return out;
}

function writeKnownShops(storage, list) {
  storage.setItem(KNOWN_SHOPS_KEY, JSON.stringify(list.slice(0, MAX_KNOWN_SHOPS)));
}

/**
 * Puts `slug` at the front of the list, keeping any name already learned for it. Only
 * applyTenantResolution() calls this — see the header for why nowhere else may. A write that
 * throws (full storage, private browsing) propagates; the caller decides how best-effort it is.
 */
export function rememberShop(storage, slug) {
  const s = cleanSlug(slug);
  if (!s) return;
  const list = readKnownShops(storage);
  const existing = list.find((e) => e.slug === s);
  const rest = list.filter((e) => e.slug !== s);
  writeKnownShops(storage, [{ slug: s, name: existing ? existing.name : '' }, ...rest]);
}

/**
 * Records the display name for a shop ALREADY on the list. Never adds a shop — a loaded config
 * is not proof the device was given that shop's link.
 */
export function nameKnownShop(storage, slug, name) {
  const s = cleanSlug(slug);
  const n = cleanName(name);
  if (!s || !n) return;
  const list = readKnownShops(storage);
  const i = list.findIndex((e) => e.slug === s);
  if (i === -1 || list[i].name === n) return;
  list[i] = { slug: s, name: n };
  writeKnownShops(storage, list);
}

/** The shops a switcher should offer: everything on the list except the one this window is on. */
export function otherKnownShops(list, currentSlug) {
  const cur = (currentSlug || '').trim().toLowerCase();
  return list.filter((e) => e.slug !== cur);
}
